import { ApiCallError } from "Infrastructure/Api/ApiUtils";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { formSetErrors } from "Infrastructure/Utilities/Forms/Helpers/formSetErrors";
import type { UseMutationOptions } from "@tanstack/react-query";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import { toast } from "sonner";

const errorResource = Resources.base.utilities.handleErrors;

export const useHandleServerError = <T extends FieldValues>(
	form: UseFormReturn<T>,
	skipToast?: boolean,
) => {
	const { handleSubmit } = form;
	const { t } = useResource();

	return {
		handleServerError: <
			TData = unknown,
			TVariables extends T = T,
			TError = Error,
			TContext = unknown,
		>(
			mutateFn: (
				variables: TVariables,
				options?: UseMutationOptions<TData, TError, TVariables, TContext>,
			) => void,
			options?: UseMutationOptions<TData, TError, TVariables, TContext>,
		) => {
			handleSubmit(
				(data) =>
					mutateFn(data as TVariables, {
						...options,
						onSuccess: (data, variables, context, ...args) => {
							options?.onSuccess?.(data, variables, context, ...args);
						},
						onError: (error: TError, variables, context, ...args) => {
							options?.onError?.(error, variables, context, ...args);
							if (error instanceof ApiCallError) {
								console.error(error);
								error &&
									!skipToast &&
									toast.warning(error.message || t(errorResource.reviewFields));

								formSetErrors(form, error);
							}
						},
					}),
				(errors) => {
					console.error(errors);
					errors && !skipToast && toast.warning(t(errorResource.reviewFields));
				},
			)();
		},
	};
};
